import React from 'react';

type FormCardProps = {
    title: string;
    /** Completes `form-card-…`, so a test names the section rather than counting cards. */
    testId: string;
    description?: string;
    children: React.ReactNode;
};

/**
 * One section of a form, in a frame of its own.
 *
 * The long forms in this admin area were a single run of fieldsets, which gave a reader no way
 * to see where one concern ended and the next began. The project definition screens already
 * solve this with white cards on the page ground, so these wear the same frame — a super user
 * moving between the two should not have to learn a second layout.
 *
 * The heading is a real `h3`: a section of a form has to be reachable by anyone navigating by
 * headings, and a styled `div` is not.
 */
const FormCard = ({ title, testId, description, children }: FormCardProps) => (
    <section
        className='flex w-full flex-col rounded-xl border border-border bg-white p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-shadow hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
        data-testid={`form-card-${testId}`}
    >
        {/* The gradient rule under the title is the project definition screens' own, kept so the
            two read as one product rather than two. */}
        <h3 className='relative mb-4 flex items-center justify-between pb-4 text-lg font-light tracking-tight text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-primary after:to-primary/10'>
            {title}
        </h3>

        {description && (
            <p className='mb-4 text-xs text-muted-foreground' data-testid={`form-card-${testId}-description`}>
                {description}
            </p>
        )}

        <div className='flex flex-col gap-4'>{children}</div>
    </section>
);

export default FormCard;
